/**
 * /operations/transactions/attendance/new — T&A HR-initiate form
 * (M30.5.4).
 */

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { EmployeePicker } from "../../components/ui/EmployeePicker";
import { TransactionFormShell } from "../../components/hr_operations";
import { api, type EmployeePickerOption } from "../../lib/api";
import { useHrActor } from "../../lib/use-hr-actor";
import { useTransactionAssist } from "../../lib/use-transaction-assist";

export function AttendanceHrInitiatePage() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.resolvedLanguage === "ar";
  const navigate = useNavigate();

  const [employee, setEmployee] = useState<EmployeePickerOption | null>(
    null,
  );
  const [reasonCode, setReasonCode] = useState<string>("");
  const [explanation, setExplanation] = useState<string>("");
  const [attendanceDate, setAttendanceDate] = useState<string>("");
  const [clockIn, setClockIn] = useState<string>("");
  const [clockOut, setClockOut] = useState<string>("");
  const [notes, setNotes] = useState<string>("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const reasonCodes = useQuery({
    queryKey: ["reason-codes", "attendance"],
    queryFn: () => api.listReasonCodes("attendance"),
    staleTime: 5 * 60_000,
  });

  // Actor identity from Clerk (audit fix C1 — commandment 39).
  const actor = useHrActor();

  const enabled =
    actor.isReady && !!employee && !!reasonCode && !!attendanceDate;
  const body = {
    actor_user_id: actor.actorUserId,
    actor_role: actor.actorRole,
    target_employee_id: employee?.id ?? "",
    reason_code: reasonCode,
    reason_explanation: explanation || null,
    attendance_date: attendanceDate,
    clock_in_time: clockIn ? `${attendanceDate}T${clockIn}:00Z` : null,
    clock_out_time: clockOut
      ? `${attendanceDate}T${clockOut}:00Z`
      : null,
    expected_shift_id: null,
    notes: notes || null,
  };
  const { result, isLoading, error, acknowledgedCodes, acknowledge } =
    useTransactionAssist({
      body,
      enabled,
      callAssist: (b) => api.attendanceHrInitiateAssist(b),
    });

  async function submit() {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await api.attendanceHrInitiateCommit({
        ...body,
        ai_findings_acknowledged: Array.from(acknowledgedCodes),
      });
      setSubmitSuccess(true);
      setTimeout(() => navigate("/operations/hub"), 1500);
    } catch (e) {
      setSubmitError(String(e));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <TransactionFormShell
      transactionType="attendance"
      title={t("operations.attendance_form_title")}
      assist={result}
      isAssistLoading={isLoading}
      assistError={error}
      acknowledgedCodes={acknowledgedCodes}
      onAcknowledge={acknowledge}
      onAcceptSuggestion={(field, value) => {
        if (field === "reason_explanation" && typeof value === "string") {
          setExplanation(value);
        }
      }}
      onSubmit={submit}
      isSubmitting={isSubmitting || !actor.isReady}
      submitSuccess={submitSuccess}
      submitError={submitError}
    >
      <Field label={t("operations.form_employee_label")}>
        <EmployeePicker
          value={employee?.id ?? null}
          onChange={setEmployee}
          selected={employee}
        />
      </Field>
      <Field label={t("operations.form_reason_code_label")}>
        <select
          className="w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm text-text-primary"
          value={reasonCode}
          onChange={(e) => setReasonCode(e.target.value)}
        >
          <option value="">—</option>
          {reasonCodes.data?.codes.map((c) => (
            <option key={c.code} value={c.code}>
              {isAr ? c.display_ar : c.display_en}
            </option>
          ))}
        </select>
      </Field>
      <Field label={t("operations.attendance_form_date")}>
        <input
          type="date"
          className="w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm text-text-primary"
          value={attendanceDate}
          onChange={(e) => setAttendanceDate(e.target.value)}
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t("operations.attendance_form_clock_in")}>
          <input
            type="time"
            className="w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm text-text-primary"
            value={clockIn}
            onChange={(e) => setClockIn(e.target.value)}
            dir="ltr"
          />
        </Field>
        <Field label={t("operations.attendance_form_clock_out")}>
          <input
            type="time"
            className="w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm text-text-primary"
            value={clockOut}
            onChange={(e) => setClockOut(e.target.value)}
            dir="ltr"
          />
        </Field>
      </div>
      <Field label={t("operations.form_reason_explanation_label")}>
        <textarea
          className="w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm text-text-primary"
          rows={3}
          value={explanation}
          onChange={(e) => setExplanation(e.target.value)}
        />
      </Field>
      <Field label={t("operations.attendance_form_notes")}>
        <input
          type="text"
          className="w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm text-text-primary"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </Field>
    </TransactionFormShell>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-text-secondary">
        {label}
      </span>
      {children}
    </label>
  );
}
